import React, { useEffect, useState } from 'react';
import { fluxosAPI } from '../../services/api';

/**
 * Valores dos parâmetros do fluxo para ESTE canal.
 *
 * É a peça que faz o reaproveitamento acontecer: 3 unidades apontando para o
 * mesmo fluxo, cada uma com seu nome, horário e link. Antes, cada unidade
 * exigia uma cópia do fluxo — e mudar uma mensagem significava editar N vezes.
 */
export default function ValoresPorCanal({ fluxoId, instanciaId, darkMode, valores, onChange }) {
  const [declaradas, setDeclaradas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!fluxoId || !instanciaId) {
      setDeclaradas([]);
      return;
    }
    let cancelado = false;
    setCarregando(true);
    setErro('');
    // As duas chamadas sao independentes: um erro na de valores nao pode
    // esconder a declaracao e fazer o painel desaparecer como se o fluxo nao
    // tivesse parametro nenhum.
    Promise.allSettled([
      fluxosAPI.getVariaveis(fluxoId),
      fluxosAPI.getValores(fluxoId, 'instancia', instanciaId),
    ])
      .then(([decl, vals]) => {
        if (cancelado) return;
        if (decl.status === 'fulfilled') {
          setDeclaradas(decl.value.data?.data || []);
        } else {
          setDeclaradas([]);
          setErro('Não foi possível carregar os parâmetros deste fluxo.');
          return;
        }
        if (vals.status === 'fulfilled') {
          onChange(vals.value.data?.data || {});
        } else {
          setErro('Os parâmetros carregaram, mas os valores deste canal não.');
        }
      })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fluxoId, instanciaId]);

  if (!fluxoId || !instanciaId || carregando) return null;
  if (erro) {
    return <p className="text-xs text-amber-500 mt-3">{erro}</p>;
  }
  if (declaradas.length === 0) return null;

  const cls = darkMode
    ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  // Sem valor E sem padrao a mensagem sai com um buraco, seja a variavel
  // obrigatoria ou nao — por isso nao filtro por `obrigatoria` aqui.
  const faltando = declaradas.filter((v) => !valores?.[v.chave] && !v.padrao);

  return (
    <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-gray-50 border-gray-200'}`}>
      <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Valores deste canal
      </p>
      <div className="space-y-2">
        {declaradas.map((v) => (
          <div key={v.chave}>
            <label className={`block text-[11px] mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {v.label || v.chave}
              {v.obrigatoria && <span className="text-red-500"> *</span>}
              <code className={`ml-1.5 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                {'{{' + v.chave + '}}'}
              </code>
            </label>
            <input
              value={valores?.[v.chave] || ''}
              onChange={(e) => onChange({ ...(valores || {}), [v.chave]: e.target.value })}
              placeholder={v.padrao ? `Padrão: ${v.padrao}` : 'Sem valor'}
              className={`w-full px-3 py-1.5 text-sm rounded-lg border ${cls}`}
            />
          </div>
        ))}
      </div>

      {faltando.length > 0 && (
        <p className="text-xs text-amber-500 mt-2">
          Sem valor e sem padrão: {faltando.map((v) => v.chave).join(', ')} — a
          mensagem sairia com um espaço no lugar da variável.
        </p>
      )}

    </div>
  );
}
